import { Chess } from "chess.js";

/**
 * Movable color configuration for the physical board.
 * - "both": Physical player can lift/move both white and black pieces (e.g. analysis).
 * - "white": Physical player can only move white pieces.
 * - "black": Physical player can only move black pieces.
 * - "none": Piece manipulation is disabled (e.g. edit mode, opponent's turn, game over).
 */
export type MovableColor = "white" | "black" | "both" | "none";

/**
 * Game termination / winner state.
 * - "white": White won (e.g., Black is checkmated or resigned).
 * - "black": Black won (e.g., White is checkmated or resigned).
 * - "draw": Draw (stalemate, repetition, 50-move rule, insufficient material, agreed draw).
 * - "none": Game in progress, no winner yet.
 */
export type Winner = "none" | "white" | "black" | "draw";

/**
 * Setup packet parameters sent from App to Board.
 * Format: SETUP:<FEN>:<MOVABLE_COLOR>:<WINNER>:<GUIDING_MOVE>
 */
export interface SetupPacket {
  fen: string;
  movableColor: MovableColor;
  winner: Winner;
  guidingMove?: string;
}

/**
 * Board -> App in-progress & completed move states.
 */
export type BoardStateType =
  | "MOVED"
  | "CAPTURED"
  | "CASTLE_FINISHED"
  | "PROMOTING"
  | "PICKED"
  | "NO_CHANGE"
  | "CAPTURE_IN_PROGRESS"
  | "CASTLE_IN_PROGRESS"
  | "INVALID";

export interface BoardStatePacket {
  type: BoardStateType;
  raw: string;
  from?: string;
  to?: string;
  square?: string;
  kingFrom?: string;
  kingTo?: string;
  kingState?: number;
  rookState?: number;
  pickedPieces?: string[];
  placedPieces?: string[];
}

/**
 * Determine winner status from a chess.js instance.
 */
export function getWinnerFromChess(chess: Chess): Winner {
  if (chess.isCheckmate()) {
    // If it is White's turn and White is in checkmate, Black wins.
    return chess.turn() === "w" ? "black" : "white";
  }
  if (chess.isDraw()) {
    return "draw";
  }
  return "none";
}

/**
 * Safely compute winner status from an arbitrary FEN string.
 */
export function getWinnerFromFen(fen: string): Winner {
  try {
    const chess = new Chess();
    chess.load(fen);
    return getWinnerFromChess(chess);
  } catch {
    return "none";
  }
}

/**
 * Serialize a SetupPacket to protocol string:
 * `SETUP:<FEN>:<MOVABLE_COLOR>:<WINNER>:<GUIDING_MOVE>`
 */
export function createSetupPacket(packet: SetupPacket): string {
  const guidingMove = packet.guidingMove || "none";
  return `SETUP:${packet.fen}:${packet.movableColor}:${packet.winner}:${guidingMove}`;
}

/**
 * Helper to generate the SETUP packet specifically for the Analysis Board.
 * - In Edit Mode: movableColor is "none", winner evaluated from FEN, guidingMove is "none".
 * - In Analysis Mode: movableColor is "both", winner evaluated from chess instance or FEN, guidingMove is last move in UCI format.
 */
export function createAnalysisSetupPacket(
  fen: string,
  isEditMode: boolean,
  chess?: Chess,
  lastMove?: { from: string; to: string; promotion?: string } | null
): string {
  const movableColor: MovableColor = isEditMode ? "none" : "both";
  const winner: Winner =
    !isEditMode && chess ? getWinnerFromChess(chess) : getWinnerFromFen(fen);

  let guidingMove = "none";
  if (!isEditMode) {
    if (lastMove) {
      guidingMove = `${lastMove.from}${lastMove.to}${lastMove.promotion || ""}`;
    } else if (chess) {
      const history = chess.history({ verbose: true });
      if (history.length > 0) {
        const latest = history[history.length - 1];
        guidingMove = `${latest.from}${latest.to}${latest.promotion || ""}`;
      }
    }
  }

  return createSetupPacket({
    fen,
    movableColor,
    winner,
    guidingMove,
  });
}

/**
 * Helper to generate the SETUP packet specifically for a live game.
 * - If game is finished / over: movableColor is "none", winner evaluated, guidingMove is last move.
 * - If game in progress:
 *     - If it's the player's turn: movableColor is playerColor ("white" | "black").
 *     - If it's the opponent's turn: movableColor is "none".
 *     - winner is "none".
 *     - guidingMove is last move in UCI format.
 */
export function createLiveGameSetupPacket(
  fen: string,
  playerColor: "white" | "black",
  chess?: Chess,
  lastMove?: { from: string; to: string; promotion?: string } | null,
  isGameOver?: boolean
): string {
  const winner: Winner =
    isGameOver || (chess && chess.isGameOver())
      ? chess
        ? getWinnerFromChess(chess)
        : getWinnerFromFen(fen)
      : "none";

  let movableColor: MovableColor = "none";
  if (winner === "none" && !isGameOver) {
    if (chess) {
      const currentTurn = chess.turn();
      const isPlayerTurn =
        (currentTurn === "w" && playerColor === "white") ||
        (currentTurn === "b" && playerColor === "black");
      movableColor = isPlayerTurn ? playerColor : "none";
    } else {
      const fenTurn = fen.split(" ")[1] || "w";
      const isPlayerTurn =
        (fenTurn === "w" && playerColor === "white") ||
        (fenTurn === "b" && playerColor === "black");
      movableColor = isPlayerTurn ? playerColor : "none";
    }
  }

  let guidingMove = "none";
  if (lastMove) {
    guidingMove = `${lastMove.from}${lastMove.to}${lastMove.promotion || ""}`;
  } else if (chess) {
    const history = chess.history({ verbose: true });
    if (history.length > 0) {
      const latest = history[history.length - 1];
      guidingMove = `${latest.from}${latest.to}${latest.promotion || ""}`;
    }
  }

  return createSetupPacket({
    fen,
    movableColor,
    winner,
    guidingMove,
  });
}


/**
 * Parse an incoming message from the ESP32 Board to structured data.
 */
export function parseBoardMessage(msg: string): BoardStatePacket | null {
  if (!msg.startsWith("STATE:")) {
    return null;
  }

  const parts = msg.split(":");
  const stateType = parts[1] as BoardStateType;

  switch (stateType) {
    case "MOVED":
    case "CAPTURED":
    case "PROMOTING": {
      // Format: STATE:MOVED:<FROM>><TO> or STATE:CAPTURED:<FROM>><TO> or legacy STATE:MOVED:<FROM>:<TO>
      let from = "";
      let to = "";
      if (parts[2] && parts[2].includes(">")) {
        const [f, t] = parts[2].split(">");
        from = f;
        to = t;
      } else if (parts[2] && parts[3]) {
        from = parts[2];
        to = parts[3];
      }
      return {
        type: stateType,
        raw: msg,
        from,
        to,
      };
    }

    case "CASTLE_FINISHED": {
      return {
        type: "CASTLE_FINISHED",
        raw: msg,
      };
    }

    case "PICKED": {
      // Format: STATE:PICKED:<SQUARE>
      return {
        type: "PICKED",
        raw: msg,
        square: parts[2],
      };
    }

    case "NO_CHANGE": {
      return {
        type: "NO_CHANGE",
        raw: msg,
      };
    }

    case "CAPTURE_IN_PROGRESS": {
      // Format: STATE:CAPTURE_IN_PROGRESS:<FROM>><TO> or STATE:CAPTURE_IN_PROGRESS:<FROM>:<TO>
      let from = "";
      let to = "";
      if (parts[2] && parts[2].includes(">")) {
        const [f, t] = parts[2].split(">");
        from = f;
        to = t;
      } else if (parts[2] && parts[3]) {
        from = parts[2];
        to = parts[3];
      }
      return {
        type: "CAPTURE_IN_PROGRESS",
        raw: msg,
        from,
        to,
      };
    }

    case "CASTLE_IN_PROGRESS": {
      // Format: STATE:CASTLE_IN_PROGRESS:<K_FROM>><K_TO>:<K_STATE>:<R_STATE>
      let kingFrom = "";
      let kingTo = "";
      let kingState = 0;
      let rookState = 0;

      if (parts[2] && parts[2].includes(">")) {
        const [kf, kt] = parts[2].split(">");
        kingFrom = kf;
        kingTo = kt;
        kingState = parseInt(parts[3] || "0", 10);
        rookState = parseInt(parts[4] || "0", 10);
      } else {
        // Fallback for multi-colon format
        kingFrom = parts[2] || "";
        kingTo = parts[3] || "";
        kingState = parseInt(parts[4] || "0", 10);
        rookState = parseInt(parts[5] || "0", 10);
      }

      return {
        type: "CASTLE_IN_PROGRESS",
        raw: msg,
        kingFrom,
        kingTo,
        kingState,
        rookState,
      };
    }

    case "INVALID": {
      // Format: STATE:INVALID:<PICKED_PIECES>;<PLACED_PIECES>
      const subParts = (parts[2] || "").split(";");
      const pickedPieces = subParts[0] ? subParts[0].split(",").filter(Boolean) : [];
      const placedPieces = subParts[1] ? subParts[1].split(",").filter(Boolean) : [];

      return {
        type: "INVALID",
        raw: msg,
        pickedPieces,
        placedPieces,
      };
    }

    default:
      return null;
  }
}
